import { X, Loader2 } from "lucide-react";
import { useState } from "react";

/**
 * BulkActionBar — floating bottom bar that appears whenever one or more rows
 * are selected in an admin list. Actions is an array of:
 *   { key, label, icon, variant: "default"|"gold"|"danger", confirm?: string, onClick: async (ids) => {} }
 *
 * Each action runs on the current `ids` prop (the selected IDs). After a
 * successful action, the component calls `onClear()` to reset the selection
 * in the parent.
 */
export default function BulkActionBar({ count, ids = [], actions = [], onClear, label = "selecionada(s)" }) {
  const [busyKey, setBusyKey] = useState(null);
  if (count === 0) return null;

  async function run(action) {
    if (action.confirm && !window.confirm(action.confirm.replace("{count}", count))) return;
    setBusyKey(action.key);
    try {
      await action.onClick(Array.from(ids));
      onClear?.();
    } catch {
      // Individual actions are expected to toast their own errors.
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[min(95vw,880px)] lux-card px-4 py-3 flex items-center gap-3 flex-wrap shadow-2xl"
         style={{ background: "#041f1b", borderColor: "#d4af37" }}
         data-testid="bulk-action-bar"
         role="toolbar"
         aria-label="Ações em massa">
      <div className="flex items-center gap-2 text-sm text-[#fdfbf7]">
        <span className="inline-flex items-center justify-center min-w-[28px] h-[28px] px-2 rounded-sm bg-[#d4af37] text-[#041f1b] text-xs font-mono font-semibold">
          {count}
        </span>
        <span className="text-[#a0b2af]">{label}</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap ml-auto">
        {actions.map((a) => {
          const disabled = busyKey && busyKey !== a.key;
          const busy = busyKey === a.key;
          const variantClass = a.variant === "danger"
            ? "border-[#ef4444]/60 text-[#fca5a5] hover:bg-[#ef4444]/10"
            : a.variant === "gold"
              ? "border-[#d4af37] text-[#041f1b] bg-[#d4af37] hover:bg-[#d4af37]/90"
              : "border-[#1a4640] text-[#fdfbf7] hover:border-[#d4af37] hover:text-[#d4af37]";
          const Icon = a.icon;
          return (
            <button key={a.key}
                    disabled={disabled || busy}
                    onClick={() => run(a)}
                    className={`px-3.5 py-2 text-[11px] uppercase tracking-widest border rounded-sm flex items-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${variantClass}`}
                    data-testid={`bulk-action-${a.key}`}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : Icon ? <Icon size={12} /> : null}
              {a.label}
            </button>
          );
        })}
        <button onClick={onClear}
                disabled={!!busyKey}
                className="p-2 text-[#a0b2af] hover:text-[#fdfbf7] disabled:opacity-50"
                data-testid="bulk-action-clear"
                aria-label="Limpar seleção">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

/**
 * Row-level checkbox with consistent styling across all admin lists.
 * Use `data-testid` on the parent row; this component already sets its own.
 */
export function BulkCheckbox({ checked, onChange, indeterminate = false, id, label = "Selecionar", testId }) {
  return (
    <label className="inline-flex items-center justify-center cursor-pointer select-none"
           onClick={(e) => e.stopPropagation()}
           title={label}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange?.(e.target.checked, id)}
        ref={(el) => { if (el) el.indeterminate = !!indeterminate; }}
        className="w-4 h-4 accent-[#d4af37] cursor-pointer"
        data-testid={testId || (id ? `bulk-select-${id}` : "bulk-select-all")}
        aria-label={label}
      />
    </label>
  );
}
